import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Secure token helpers (match AuthContext)
const tokenKey = "___at";
const readToken = () => {
  try { const v = sessionStorage.getItem(tokenKey); return v ? atob(v) : null; } catch (e) { console.warn("Token read failed:", e.message); return null; }
};
const clearToken = () => {
  try { sessionStorage.removeItem(tokenKey); localStorage.removeItem("token"); } catch (e) { console.warn("Token clear failed:", e.message); }
};

api.interceptors.request.use((config) => {
  const token = readToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track in-flight refresh to dedupe concurrent 401s
let refreshing = null;
const attemptRefresh = async () => {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      // /auth/refresh sets a fresh httpOnly access_token cookie and returns only
      // a message, never a token in the body. The backend reads that cookie
      // before the Authorization header, so a successful refresh is enough.
      await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
      return true;
    } catch (e) { /* fall through */ }
    return false;
  })();
  const ok = await refreshing;
  refreshing = null;
  return ok;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    if (error.response?.status === 401 && !original._retry && !url.includes("/auth/login") && !url.includes("/auth/refresh")) {
      original._retry = true;
      const refreshed = await attemptRefresh();
      if (refreshed) {
        // The backend reads the freshly set access_token cookie before the
        // Authorization header, so retrying as-is now authenticates.
        return api(original);
      }
      clearToken();
      if (window.location.pathname !== "/login" && window.location.pathname !== "/register") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Workspace API
export const workspaceApi = {
  activity: (limit=20, type, before) => api.get(`/workspace/activity`, { params: { limit, type, before } }),
  leadSources: (chatbotId) => api.get(`/workspace/leads/sources`, { params: { chatbot_id: chatbotId } }),
  conversationTags: () => api.get(`/workspace/conversations/tags`),
  analyticsExportCsv: (days=30) => api.get(`/workspace/analytics/export.csv`, { params: { days }, responseType: "blob" }),
  chatbotsPerformance: (days=30) => api.get(`/workspace/chatbots/performance`, { params: { days } }),
  leadsTimeline: (days=30) => api.get(`/workspace/leads/timeline`, { params: { days } }),
  dashboardSummary: (days=7) => api.get(`/workspace/dashboard/summary`, { params: { days } }),
  searchMessages: (q, limit=25) => api.get(`/workspace/messages/search`, { params: { q, limit } }),
  leadsFunnel: (days=30) => api.get(`/workspace/leads/funnel`, { params: { days } }),
  digest: (days=1) => api.get(`/workspace/digest`, { params: { days } }),
  activityExportCsv: (days=30) => api.get(`/workspace/activity/export.csv`, { params: { days }, responseType: "blob" }),
  leadsDigest: (hours=24) => api.get(`/workspace/leads/digest`, { params: { hours } }),
  allTags: () => api.get(`/workspace/tags/all`),
  leadsByChatbot: (days=30) => api.get(`/workspace/leads/by-chatbot`, { params: { days } }),
  messagesCount: (days=30) => api.get(`/workspace/messages/count`, { params: { days } }),
  conversationsByDay: (days=14) => api.get(`/workspace/conversations/by-day`, { params: { days } }),
  recentChatbots: (limit=5) => api.get(`/workspace/chatbots/recent`, { params: { limit } }),
  recentMessages: (limit=10) => api.get(`/workspace/messages/recent`, { params: { limit } }),
  recentLeads: (limit=10) => api.get(`/workspace/leads/recent`, { params: { limit } }),
  convAvgLength: (days=30) => api.get(`/workspace/conversations/avg-length`, { params: { days } }),
  ratingsSummary: (days=30) => api.get(`/workspace/ratings/summary`, { params: { days } }),
  ratingsDaily: (days=30) => api.get(`/workspace/ratings/daily`, { params: { days } }),
  ratingsHourly: (days=30) => api.get(`/workspace/ratings/hourly`, { params: { days } }),
  get: () => api.get("/workspace"),
  update: (data) => api.patch("/workspace", data),
  getUsage: () => api.get("/workspace/usage"),
  getAnalytics: (params) => api.get("/workspace/analytics", { params }),
  getErrors: (limit = 100) => api.get(`/workspace/errors?limit=${limit}`),
  clearErrors: () => api.delete(`/workspace/errors`),
  clearOldErrors: (days=30) => api.delete(`/workspace/errors/old`, { params: { days } }),
  deleteError: (errorId) => api.delete(`/workspace/errors/${errorId}`),
  updateApiKey: (provider, api_key) => api.post("/workspace/keys", { provider, api_key }),
  deleteApiKey: (provider) => api.delete(`/workspace/keys/${provider}`),
  getTeam: () => api.get("/workspace/team"),
  inviteTeamMember: (email, role) => api.post("/workspace/team/invite", { email, role }),
  removeTeamMember: (memberId) => api.delete(`/workspace/team/${memberId}`),
  // Batch 18: cache + hourly
  cacheSummary: () => api.get("/workspace/cache/summary"),
  messagesByHour: (days=7) => api.get("/workspace/messages/by-hour", { params: { days } }),
  // Batch 20: failing intents + length distribution + cache rate
  failingIntents: (days=7, limit=10) => api.get("/workspace/analytics/failing-intents", { params: { days, limit } }),
  lengthDistribution: (days=30) => api.get("/workspace/conversations/length-distribution", { params: { days } }),
  cacheRate: (days=7) => api.get("/workspace/messages/cache-rate", { params: { days } }),
  conversationsSearch: (q, limit=20) => api.get("/workspace/conversations/search", { params: { q, limit } }),
  cacheRateTrend: (days=14) => api.get("/workspace/messages/cache-rate-trend", { params: { days } }),
  topPages: (days=7, limit=10) => api.get("/workspace/analytics/top-pages", { params: { days, limit } }),
  utmBreakdown: (days = 30) => api.get("/workspace/analytics/utm-breakdown", { params: { days } }),
  botPerformance: (days = 30) => api.get("/workspace/analytics/bot-performance", { params: { days } }),
  usageDaily: (days=30) => api.get("/workspace/usage/daily", { params: { days } }),
  messagesHourly: (days=7) => api.get("/workspace/messages/hourly", { params: { days } }),
};

// Chatbot API
export const chatbotApi = {
  list: (status) => api.get("/chatbots", { params: { status } }),
  get: (id) => api.get(`/chatbots/${id}`),
  createShortLink: (id, data) => api.post(`/chatbots/${id}/short-link`, data),
  listShortLinks: (id) => api.get(`/chatbots/${id}/short-links`),
  deleteShortLink: (linkId) => api.delete(`/short-links/${linkId}`),
  create: (data) => api.post("/chatbots", data),
  update: (id, data) => api.patch(`/chatbots/${id}`, data),
  delete: (id) => api.delete(`/chatbots/${id}`),
  remove: (id) => api.delete(`/chatbots/${id}`),
  duplicate: (id) => api.post(`/chatbots/${id}/duplicate`),
  train: (id) => api.post(`/chatbots/${id}/train`),
  getStatus: (id) => api.get(`/chatbots/${id}/status`),
  getSources: (id) => api.get(`/chatbots/${id}/sources`),
  addSource: (id, data) => api.post(`/chatbots/${id}/sources`, data),
  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/chatbots/${id}/sources/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteSource: (chatbotId, sourceId) => api.delete(`/chatbots/${chatbotId}/sources/${sourceId}`),
  // BC-009: permanently mask personal data inside a training source
  redactSourcePii: (chatbotId, sourceId) => api.post(`/chatbots/${chatbotId}/sources/${sourceId}/redact-pii`),
  // BC-018: extra lead email recipients
  listLeadRecipients: (id) => api.get(`/chatbots/${id}/lead-recipients`),
  addLeadRecipient: (id, email) => api.post(`/chatbots/${id}/lead-recipients`, { email }),
  resendLeadRecipient: (id, rid) => api.post(`/chatbots/${id}/lead-recipients/${rid}/resend`),
  deleteLeadRecipient: (id, rid) => api.delete(`/chatbots/${id}/lead-recipients/${rid}`),
  setLeadOwnerEmail: (id, enabled) => api.patch(`/chatbots/${id}/lead-recipients/owner`, { enabled }),
  // BC-019: chatbot co-owners
  listCoOwners: (id) => api.get(`/chatbots/${id}/co-owners`),
  inviteCoOwner: (id, email, role) => api.post(`/chatbots/${id}/co-owners`, { email, role }),
  changeShareRole: (id, accessId, role) => api.patch(`/chatbots/${id}/co-owners/${accessId}`, { role }),
  resendCoOwner: (id, accessId) => api.post(`/chatbots/${id}/co-owners/${accessId}/resend`),
  removeCoOwner: (id, accessId) => api.delete(`/chatbots/${id}/co-owners/${accessId}`),
  leaveShared: (id) => api.post(`/chatbots/${id}/leave`),
  chat: (id, message, sessionId, visitorId) => api.post(`/chatbots/${id}/chat`, { message, session_id: sessionId, visitor_id: visitorId }),
  // Avatar
  uploadAvatar: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/chatbots/${id}/avatar`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteAvatar: (id) => api.delete(`/chatbots/${id}/avatar`),
  // Leads
  getLeads: (id, params) => api.get(`/chatbots/${id}/leads`, { params }),
  // Proactive Analytics
  proactiveAnalytics: (id, days=7) => api.get(`/chatbots/${id}/analytics/proactive`, { params: { days } }),
  health: (id) => api.get(`/chatbots/${id}/health`),
  ratings: (id, days=30) => api.get(`/chatbots/${id}/ratings`, { params: { days } }),
  recentConversations: (id, limit=10) => api.get(`/chatbots/${id}/conversations/recent`, { params: { limit } }),
  setNotes: (id, notes) => api.post(`/chatbots/${id}/notes`, { notes }),
  // Batch 18: response cache
  cacheStats: (id) => api.get(`/chatbots/${id}/cache/stats`),
  cacheClear: (id) => api.delete(`/chatbots/${id}/cache`),
  cacheUpdateSettings: (id, settings) => api.post(`/chatbots/${id}/cache/settings`, settings),
  cacheWarmup: (id) => api.post(`/chatbots/${id}/cache/warmup`),
  cachePrune: (id, days=30) => api.post(`/chatbots/${id}/cache/prune`, null, { params: { days } }),
  cacheCostHistory: (id, days=14) => api.get(`/chatbots/${id}/cache/cost-history`, { params: { days } }),
};

// Leads API
export const leadsApi = {
  list: (params) => api.get("/workspace/leads", { params }),
  create: (data) => api.post("/leads", data),
  delete: (id) => api.delete(`/leads/${id}`),
  remove: (id) => api.delete(`/leads/${id}`),
  update: (id, data) => api.patch(`/leads/${id}`, data),
  bulkUpdate: (ids, opts={}) => api.post("/leads/bulk-update", { ids, status: opts.status, add_tags: opts.addTags, remove_tags: opts.removeTags }),
  bulkDelete: (ids) => api.post("/leads/bulk-delete", { ids }),
  exportCsv: (chatbotId) => api.get("/workspace/leads/export", { params: { chatbot_id: chatbotId }, responseType: "blob" }),
  conversation: (id) => api.get(`/leads/${id}/conversation`),
  export: (params) => api.get("/workspace/leads/export", { params, responseType: "blob" }),
  listNotes: (id) => api.get(`/leads/${id}/notes`),
  addNote: (id, text) => api.post(`/leads/${id}/notes`, { text }),
  deleteNote: (id, noteId) => api.delete(`/leads/${id}/notes/${noteId}`),
  previewDedupe: () => api.get("/leads/dedupe-preview"),
  mergeLeads: (keepId, mergeIds) => api.post("/leads/merge", { keep_id: keepId, merge_ids: mergeIds }),
};

// LLM providers & models
export const llmApi = {
  listProviders: () => api.get("/llm/providers"),
  upsertProvider: (data) => api.post("/llm/providers", data),
  testProvider: (data) => api.post("/llm/providers/test", data),
  deleteProvider: (id) => api.delete(`/llm/providers/${id}`),
  listModels: (provider) => api.get("/llm/models", { params: { provider } }),
  catalog: (q) => api.get("/llm/catalog", { params: { q } }),
};

// Sign-in methods this deployment offers, and Google sign-in itself.
export const authApi = {
  config: () => api.get("/auth/config"),
  google: (credential) => api.post("/auth/google", { credential }),
  // Always resolves the same way whether or not the address is registered.
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) => api.post("/auth/reset-password", { token, password }),
};

// Conversations API
export const conversationApi = {
  exportText: (id) => api.get(`/conversations/${id}/export`, { responseType: "text" }),
  /* One conversation as JSON. Named distinctly from exportAllJson below:
     both used to be called exportJson, so the workspace export silently
     shadowed this one and per-conversation JSON export never worked. */
  exportOneJson: (id) => api.get(`/conversations/${id}/export.json`),
  exportHtmlUrl: (id) => `${api.defaults.baseURL}/conversations/${id}/export.html`,
  update: (id, data) => api.patch(`/conversations/${id}`, data),
  list: (chatbotId, search, before, limit, fromDate, toDate) => api.get("/conversations", { params: { chatbot_id: chatbotId, search: search || undefined, before, limit, from_date: fromDate || undefined, to_date: toDate || undefined } }),
  get: (id) => api.get(`/conversations/${id}`),
  getMessages: (id) => api.get(`/conversations/${id}/messages`),
  takeover: (id) => api.post(`/conversations/${id}/takeover`),
  release: (id) => api.post(`/conversations/${id}/release`),
  reply: (id, message) => api.post(`/conversations/${id}/reply`, { message }),
  exportAllJson: (chatbotId) => api.get("/workspace/conversations/export", { params: { chatbot_id: chatbotId }, responseType: "blob" }),
  delete: (id) => api.delete(`/conversations/${id}`),
  bulkTag: (ids, addTags=[], removeTags=[]) => api.post("/conversations/bulk-tag", { ids, add_tags: addTags, remove_tags: removeTags }),
  bulkDelete: (ids) => api.post("/conversations/bulk-delete", { ids }),
};

// Appointments API
export const appointmentApi = {
  list: (params) => api.get("/appointments", { params }),
  create: (data) => api.post("/appointments", data),
  update: (id, data) => api.patch(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`)
};

// Webhooks API
export const webhookApi = {
  list: () => api.get("/webhooks"),
  create: (data) => api.post("/webhooks", data),
  update: (id, data) => api.patch(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  getSecret: (id) => api.get(`/webhooks/${id}/secret`),
  rotateSecret: (id) => api.post(`/webhooks/${id}/rotate-secret`),
  test: (id) => api.post(`/webhooks/${id}/test`),
  listDeliveries: (id, limit=50) => api.get(`/webhooks/${id}/deliveries`, { params: { limit } }),
  retryDelivery: (deliveryId) => api.post(`/webhooks/deliveries/${deliveryId}/retry`),
  deliveriesSummary: (days=7) => api.get(`/webhooks/deliveries/summary`, { params: { days } }),
};

// Templates API
export const templateApi = {
  list: () => api.get("/templates")
};

export default api;

export const messagesApi = {
  rate: (messageId, rating, sessionId, comment) => api.post(`/messages/${messageId}/rate`, { rating, session_id: sessionId, comment }),
};
